import { useEffect, useRef, useState } from "react";
import { Animated, View, Text, Pressable, ScrollView, Dimensions, Platform } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useRouter } from "expo-router";
import { useNotifications } from "@/hooks/useNotifications";
import { colors, radius } from "@/lib/theme";
import type { UserNotification } from "@/lib/types";

function timeAgo(ts: any): string {
  if (!ts?.toDate) return "";
  const diff = Date.now() - ts.toDate().getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

const CATEGORY_COLOR: Record<string, { bg: string; text: string }> = {
  fixture_created: { bg: "#dbeafe", text: "#1d4ed8" },
  training_created: { bg: "#fef3c7", text: "#b45309" },
  team_selected: { bg: "#dcfce7", text: "#15803d" },
  team_announced: { bg: "#dcfce7", text: "#15803d" },
  match_cancelled: { bg: "#fee2e2", text: "#b91c1c" },
  training_cancelled: { bg: "#fee2e2", text: "#b91c1c" },
  fee_reminder: { bg: "#fef3c7", text: "#b45309" },
  fee_paid: { bg: "#dcfce7", text: "#15803d" },
  player_unavailable: { bg: "#fee2e2", text: "#b91c1c" },
  player_withdrew: { bg: "#fee2e2", text: "#b91c1c" },
  member_joined: { bg: "#ede9fe", text: "#6d28d9" },
  payment_received: { bg: "#dcfce7", text: "#15803d" },
  stripe_connected: { bg: "#dcfce7", text: "#15803d" },
  help_request: { bg: "#fef3c7", text: "#b45309" },
  generic: { bg: "#f3f4f6", text: "#374151" },
};

export default function NotificationsDrawer({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const screenWidth = Dimensions.get("window").width;
  const drawerWidth = Platform.OS === "web" ? 400 : Math.min(screenWidth * 0.88, 400);
  const slideX = useRef(new Animated.Value(drawerWidth)).current;
  const fadeOpacity = useRef(new Animated.Value(0)).current;
  const [mounted, setMounted] = useState(visible);

  useEffect(() => {
    if (visible) setMounted(true);
    Animated.parallel([
      Animated.spring(slideX, {
        toValue: visible ? 0 : drawerWidth,
        useNativeDriver: true,
        damping: 28,
        stiffness: 260,
        mass: 1,
      }),
      Animated.timing(fadeOpacity, { toValue: visible ? 1 : 0, duration: 220, useNativeDriver: true }),
    ]).start(() => {
      if (!visible) setMounted(false);
    });
  }, [visible, drawerWidth]);

  const handleTap = async (n: UserNotification & { id: string }) => {
    if (!n.read) markRead(n.id);
    onClose();
    if (n.link) {
      setTimeout(() => router.push(n.link as any), 180);
    }
  };

  if (!mounted) return null;

  return (
    <View style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, zIndex: 1000 }} pointerEvents="auto">
      <Animated.View style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.4)", opacity: fadeOpacity }}>
        <Pressable style={{ flex: 1 }} onPress={onClose} />
      </Animated.View>
      <Animated.View
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: drawerWidth,
          backgroundColor: "#f2f2f7",
          transform: [{ translateX: slideX }],
          shadowColor: "#000",
          shadowOffset: { width: -8, height: 0 },
          shadowOpacity: 0.18,
          shadowRadius: 24,
          elevation: 20,
          borderTopLeftRadius: 18,
          borderBottomLeftRadius: 18,
          overflow: "hidden",
        }}
      >
        {/* Header — iOS-style large title */}
        <View
          style={{
            paddingTop: Platform.OS === "web" ? 22 : insets.top + 8,
            paddingHorizontal: 20,
            paddingBottom: 14,
            backgroundColor: "#f2f2f7",
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
            <Text style={{ fontSize: 28, fontWeight: "800", color: colors.ink, letterSpacing: -0.5 }}>
              Notifications
            </Text>
            <Pressable
              onPress={onClose}
              hitSlop={12}
              style={({ pressed }) => ({
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: "#e5e5ea",
                alignItems: "center",
                justifyContent: "center",
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <Text style={{ fontSize: 16, color: colors.inkMuted, fontWeight: "700" }}>✕</Text>
            </Pressable>
          </View>
          {unreadCount > 0 && (
            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
              <Text style={{ fontSize: 13, color: colors.inkMuted, fontWeight: "500" }}>{unreadCount} unread</Text>
              <Pressable onPress={markAllRead} hitSlop={12}>
                <Text style={{ fontSize: 13, color: colors.brand, fontWeight: "600" }}>Mark all read</Text>
              </Pressable>
            </View>
          )}
        </View>

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: insets.bottom + 20 }}
          showsVerticalScrollIndicator={false}
        >
          {notifications.length === 0 ? (
            <View style={{ padding: 60, alignItems: "center" }}>
              <Text style={{ fontSize: 44, marginBottom: 14 }}>🎉</Text>
              <Text style={{ fontSize: 15, color: colors.inkMuted, textAlign: "center", fontWeight: "600" }}>You're all caught up</Text>
            </View>
          ) : (
            <View style={{ backgroundColor: colors.white, borderRadius: 14, overflow: "hidden", marginTop: 4 }}>
              {notifications.map((n, i) => {
                const cat = CATEGORY_COLOR[n.type] || CATEGORY_COLOR.generic;
                const isLast = i === notifications.length - 1;
                return (
                  <Pressable
                    key={n.id}
                    onPress={() => handleTap(n)}
                    style={({ pressed }) => ({
                      flexDirection: "row",
                      paddingVertical: 14,
                      paddingHorizontal: 16,
                      backgroundColor: pressed ? "#f2f2f7" : (n.read ? colors.white : "#f0f9ff"),
                      borderBottomWidth: isLast ? 0 : 0.5,
                      borderBottomColor: "#e5e5ea",
                    })}
                  >
                    <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: cat.bg, alignItems: "center", justifyContent: "center", marginRight: 12 }}>
                      <Text style={{ fontSize: 19 }}>{n.emoji || "🔔"}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                        <Text numberOfLines={1} style={{ fontSize: 15, fontWeight: n.read ? "600" : "800", color: colors.ink, flex: 1, marginRight: 8 }}>
                          {n.title}
                        </Text>
                        <Text style={{ fontSize: 12, color: colors.inkMuted, fontWeight: "500" }}>{timeAgo(n.createdAt)}</Text>
                      </View>
                      <Text numberOfLines={2} style={{ fontSize: 13, color: colors.inkMuted, marginTop: 2, lineHeight: 17 }}>{n.body}</Text>
                    </View>
                    {!n.read && (
                      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.brand, alignSelf: "center", marginLeft: 8 }} />
                    )}
                  </Pressable>
                );
              })}
            </View>
          )}
        </ScrollView>
      </Animated.View>
    </View>
  );
}
